import { createSlice } from '@reduxjs/toolkit';

export const GUIDE_STEPS = {
  NAME_AND_COVER: 0,
  TOC_BUILDER: 1,
  MODULE_SHOWCASE: 2,
  SETTINGS: 3,
};

const initialState = {
  isActive: false,
  currentStep: 0,
  startStep: 0,
  isPopulating: false,
  populationProgress: 0,

  // Name & Cover
  courseName: '',
  coverImage: null,

  // TOC Builder - local TOC structure (not yet persisted)
  tocStructure: [],

  // Module Showcase - module assignments per page
  pageModuleAssignments: {},
  selectedTemplate: null,

  // Settings (quiz/diploma auto-set by template)
  guideSettings: {
    quizEnabled: false,
    diplomaEnabled: false,
    diplomaPassPercentage: 80,
    diplomaFileId: null,
    gatedAccess: false,
    delayedAccess: false,
  },
};

const courseGuideSlice = createSlice({
  name: 'courseGuide',
  initialState,
  reducers: {
    startGuide: (state, action) => {
      const { courseName, coverImage, startStep } = action.payload || {};
      Object.assign(state, {
        ...initialState,
        isActive: true,
        currentStep: startStep || 0,
        startStep: startStep || 0,
        courseName: courseName || '',
        coverImage: coverImage || null,
      });
    },
    endGuide: () => initialState,
    setCurrentStep: (state, action) => {
      state.currentStep = action.payload;
    },
    nextStep: (state) => {
      if (state.currentStep < GUIDE_STEPS.SETTINGS) state.currentStep += 1;
    },
    prevStep: (state) => {
      if (state.currentStep > (state.startStep || 0)) state.currentStep -= 1;
    },

    // Name & Cover
    setCourseName: (state, action) => {
      state.courseName = action.payload;
    },
    setCoverImage: (state, action) => {
      state.coverImage = action.payload;
    },

    // TOC Builder
    setTocStructure: (state, action) => {
      state.tocStructure = action.payload;
    },

    // Module Showcase
    setPageModuleAssignments: (state, action) => {
      state.pageModuleAssignments = action.payload;
    },
    assignModuleToPage: (state, action) => {
      const { tempPageId, moduleType } = action.payload;
      if (!state.pageModuleAssignments[tempPageId]) {
        state.pageModuleAssignments[tempPageId] = [];
      }
      state.pageModuleAssignments[tempPageId].push({ moduleType });
    },
    removeModuleFromPage: (state, action) => {
      const { tempPageId, index } = action.payload;
      if (state.pageModuleAssignments[tempPageId]) {
        state.pageModuleAssignments[tempPageId].splice(index, 1);
      }
    },
    setSelectedTemplate: (state, action) => {
      state.selectedTemplate = action.payload;
    },

    // Settings
    setGuideSettings: (state, action) => {
      state.guideSettings = { ...state.guideSettings, ...action.payload };
    },

    // Population
    setIsPopulating: (state, action) => {
      state.isPopulating = action.payload;
    },
    setPopulationProgress: (state, action) => {
      state.populationProgress = action.payload;
    },
  },
});

export const {
  startGuide,
  endGuide,
  setCurrentStep,
  nextStep,
  prevStep,
  setCourseName,
  setCoverImage,
  setTocStructure,
  setPageModuleAssignments,
  assignModuleToPage,
  removeModuleFromPage,
  setSelectedTemplate,
  setGuideSettings,
  setIsPopulating,
  setPopulationProgress,
} = courseGuideSlice.actions;

// Selectors
export const selectGuideIsActive = (state) => state.courseGuide.isActive;
export const selectGuideCurrentStep = (state) => state.courseGuide.currentStep;
export const selectGuideCourseName = (state) => state.courseGuide.courseName;
export const selectGuideCoverImage = (state) => state.courseGuide.coverImage;
export const selectGuideTocStructure = (state) => state.courseGuide.tocStructure;
export const selectGuidePageModules = (state) => state.courseGuide.pageModuleAssignments;
export const selectGuideSelectedTemplate = (state) => state.courseGuide.selectedTemplate;
export const selectGuideSettings = (state) => state.courseGuide.guideSettings;
export const selectGuideIsPopulating = (state) => state.courseGuide.isPopulating;
export const selectGuidePopulationProgress = (state) => state.courseGuide.populationProgress;
export const selectGuideState = (state) => state.courseGuide;

export default courseGuideSlice.reducer;
